import React, { useRef, useState } from 'react';
import { ImagePlus, X } from 'lucide-react';

import { uploadImage } from '@/features/editor/api';
import { Button } from '@/components/ui/button';
import { absolutizeImageUrls } from '@/features/editor/imageUrlRewrite';

export interface FaviconFieldProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Optional site host used to resolve relative paths (e.g. `/images/foo.png`)
   * to an absolute URL for thumbnail display. When unset, relative paths
   * render against the editor's origin (which won't have the file).
   */
  siteHost?: string;
  className?: string;
}

/**
 * Favicon picker for the create / edit forms.
 *
 * Empty value renders a "No custom favicon" hint with an "Upload" button.
 * A set value renders a small thumbnail with a "Replace" / "Use default"
 * pair. Uploads go through the platform's image upload pipeline (R2 temp
 * bucket); on save, `extractR2ImageKeys` rewrites the URL to the deployed
 * `/images/<filename>` path and the workflow commits the file into the
 * site's repo at `public/images/<filename>`.
 *
 * When `value` is empty, the deployed site falls back to the template's
 * built-in `/favicon.svg` (the Landing logo) — no per-template change
 * required.
 */
export const FaviconField: React.FC<FaviconFieldProps> = ({
  value,
  onChange,
  siteHost,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewUrl = siteHost
    ? (absolutizeImageUrls(value, siteHost) as string)
    : value;

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const url = await uploadImage(file);
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      // Allow re-selecting the same file on a subsequent click.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-foreground/5 overflow-hidden"
          aria-hidden="true"
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <ImagePlus className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground">
            Site icon
          </p>
          <p className="text-xs text-muted-foreground">
            {value
              ? 'Shown in browser tabs.'
              : 'The Landing logo will be used until you upload your own.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="tertiary"
            size="dense"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : value ? 'Replace' : 'Upload'}
          </Button>
          {value && !uploading && (
            <Button
              type="button"
              variant="tertiary"
              size="icon"
              aria-label="Use default favicon"
              title="Use default favicon"
              onClick={() => onChange('')}
              className="h-9 w-9"
            >
              <X className="h-4 w-4 stroke-[1.5]" />
            </Button>
          )}
        </div>
      </div>
      {error && (
        <p className="mt-1 text-sm text-destructive">{error}</p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/x-icon,image/png,image/svg+xml,image/jpeg,image/webp"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
};
